import { Cog8ToothIcon as SettingsIcon } from '@heroicons/react/24/outline';
import {
  Bitcoin,
  CircleUserRound,
  CodeXml,
  FileDown,
  HandCoins,
  Landmark,
  LayoutDashboard,
  UsersRound,
  Wallet
} from 'lucide-react';
import type { ReactElement } from 'react';
import { useMemo } from 'react';

import { AccountStatus } from 'api';
import { Restricted } from 'containers/Restricted';

import { useContractedProducts, useVirtualAccounts } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';
import { useIsCryptoFundingEnabled } from 'hooks/useIsCryptoFundingEnabled';

import {
  ACCOUNT_WITHDRAWALS_ITEM_TEST_ID,
  ACCOUNTS_SIDEBAR_ITEM_TEST_ID,
  AUTHENTICATION_SIDEBAR_ITEM_TEST_ID,
  BATCHES_SIDEBAR_ITEM_TEST_ID,
  BUSINESS_UNITS_SIDEBAR_ITEM_TEST_ID,
  COLLECTION_SIDEBAR_ITEM_TEST_ID,
  CONFIGURATIONS_SIDEBAR_ITEM_TEST_ID,
  CRYPTO_FUNDING_SIDEBAR_COMPONENT_ID,
  CRYPTO_FUNDING_SIDEBAR_ITEM_TEST_ID,
  DEVELOPERS_SIDEBAR_ITEM_TEST_ID,
  EXPORTED_PAYINS_SIDEBAR_ITEM_TEST_ID,
  EXPORTED_PAYOUTS_SIDEBAR_ITEM_TEST_ID,
  MCP_SIDEBAR_ITEM_TEST_ID,
  EXPORTED_STATEMENTS_SIDEBAR_ITEM_TEST_ID,
  EXPORTS_SIDEBAR_ITEM_TEST_ID,
  ORDERS_SIDEBAR_ITEM_TEST_ID,
  PAYOUT_DRAFTS_PENDING_SIDEBAR_ITEM_TEST_ID,
  PAYOUT_REQUESTS_SIDEBAR_ITEM_TEST_ID,
  PAYOUTS_SIDEBAR_ITEM_TEST_ID,
  PROFILE_SIDEBAR_ITEM_TEST_ID,
  RECEIVERS_SIDEBAR_ITEM_TEST_ID,
  USER_MANAGEMENT_SIDEBAR_ITEM_TEST_ID,
  WEBHOOKS_SIDEBAR_ITEM_TEST_ID
} from './constants';
import { AccountFundingItem } from './customItems/AccountFundingItem';
import { AccountItem } from './customItems/AccountItem';
import type { SidebarMenuItem, SidebarMenuSubItem } from './SidebarItem';

/**
 *
 * This creates a list of items that should be rendered in the navbar.
 */
export const useSidebarMenu = () => {
  const {
    isManualPayoutApprovalFlowEnabled,
    isBatchesPageEnabled,
    isReceiverManagementEnabled,
    isPayoutDraftApprovalFlowEnabled,
    isWithdrawalRequestsEnabled,
    isSelfServiceDeveloperSettingsEnabled,
    isMcpTokensEnabled
  } = useFeatureAccessControl();

  const isCryptoFundingEnabled = useIsCryptoFundingEnabled();

  const { accounts: accountsInfo } = useVirtualAccounts({
    filterUnownedAccounts: true
  });
  const sortedAccountsInfo = useMemo(
    () =>
      [...accountsInfo].sort((a, b) => {
        const aIsTerminated = a.status === AccountStatus.terminated;
        const bIsTerminated = b.status === AccountStatus.terminated;

        if (aIsTerminated === bIsTerminated) {
          return 0;
        }

        return aIsTerminated ? 1 : -1;
      }),
    [accountsInfo]
  );

  const { contractedProducts } = useContractedProducts();

  const hasOnlySharedAccounts = accountsInfo.every(
    (account) => account.owner === false
  );

  const hasPayoutProducts = Boolean(
    contractedProducts?.meta?.payout_products_count
  );

  const hasCollectionProducts = Boolean(
    contractedProducts?.meta?.collection_products_count
  );

  const buildMenuItems = () => {
    const virtualAccountSidebarItems: (SidebarMenuSubItem | ReactElement)[] =
      [];

    virtualAccountSidebarItems.push(
      <Restricted
        requiredPermission="accounts:funding"
        key="account-funding-dialog-item"
      >
        <AccountFundingItem />
      </Restricted>
    );

    const payoutsSidebarItems: (SidebarMenuSubItem | ReactElement)[] = [
      {
        id: 'payout_requests',
        ['data-testid']: PAYOUT_REQUESTS_SIDEBAR_ITEM_TEST_ID,
        title: isManualPayoutApprovalFlowEnabled
          ? 'Payouts'
          : 'Payout requests',
        path: '/payouts',
        requiredPermission: 'payouts:read'
      }
    ];

    if (isWithdrawalRequestsEnabled) {
      virtualAccountSidebarItems.push({
        id: 'withdrawals',
        ['data-testid']: ACCOUNT_WITHDRAWALS_ITEM_TEST_ID,
        title: 'Withdrawals',
        path: '/accounts/withdrawals',
        requiredPermission: 'withdrawals:read'
      });
    }

    for (const accountInfo of sortedAccountsInfo) {
      virtualAccountSidebarItems.push(
        <Restricted
          requiredPermission="accounts:read"
          key={accountInfo.account_number}
        >
          <AccountItem accountInfo={accountInfo} />
        </Restricted>
      );
    }

    const exportsSidebarItems: (SidebarMenuSubItem | ReactElement)[] = [
      {
        id: 'exported_payins',
        ['data-testid']: EXPORTED_PAYINS_SIDEBAR_ITEM_TEST_ID,
        title: 'Pay-ins',
        path: '/exported_payins',
        requiredPermission: 'exports:read'
      },
      {
        id: 'exported_payouts',
        ['data-testid']: EXPORTED_PAYOUTS_SIDEBAR_ITEM_TEST_ID,
        title: 'Payouts',
        path: '/exported_payouts',
        requiredPermission: 'exports:read'
      },
      {
        id: 'exported_statements',
        ['data-testid']: EXPORTED_STATEMENTS_SIDEBAR_ITEM_TEST_ID,
        title: 'Statements',
        path: '/exported_statements',
        requiredPermission: 'exports:read'
      }
    ];

    const rootItems: SidebarMenuItem[] = [
      {
        ['data-testid']: 'dashboard',
        title: 'Dashboard',
        path: '/',
        end: true,
        icon: LayoutDashboard,
        requiredPermission: 'dashboard:read'
      }
    ];

    if (isCryptoFundingEnabled) {
      rootItems.push({
        id: CRYPTO_FUNDING_SIDEBAR_COMPONENT_ID,
        ['data-testid']: CRYPTO_FUNDING_SIDEBAR_ITEM_TEST_ID,
        title: 'Crypto funding',
        path: '/crypto_funding',
        icon: Bitcoin,
        requiredPermission: 'crypto:manage'
      });
    }

    if (hasPayoutProducts) {
      rootItems.push({
        ['data-testid']: PAYOUTS_SIDEBAR_ITEM_TEST_ID,
        title: 'Payouts',
        path: '/payouts',
        icon: HandCoins,
        subItems: payoutsSidebarItems,
        requiredPermission: 'payouts:read'
      });
      if (isPayoutDraftApprovalFlowEnabled) {
        payoutsSidebarItems.push({
          id: 'payout_drafts',
          ['data-testid']: PAYOUT_DRAFTS_PENDING_SIDEBAR_ITEM_TEST_ID,
          title: 'Payout drafts',
          path: '/payout_drafts',
          requiredPermission: 'payout_drafts:read'
        });
      }
      if (isBatchesPageEnabled) {
        payoutsSidebarItems.push({
          id: 'batches',
          ['data-testid']: BATCHES_SIDEBAR_ITEM_TEST_ID,
          title: 'Batches',
          path: '/batches',
          requiredPermission: 'payout_batches:read'
        });
      }
      if (isReceiverManagementEnabled) {
        payoutsSidebarItems.push({
          id: 'receivers',
          ['data-testid']: RECEIVERS_SIDEBAR_ITEM_TEST_ID,
          title: 'Receivers',
          path: '/receivers',
          requiredPermission: 'receivers:read'
        });
      }
    }

    if (hasCollectionProducts) {
      rootItems.push({
        ['data-testid']: COLLECTION_SIDEBAR_ITEM_TEST_ID,
        title: 'Pay-ins',
        path: '/collections',
        icon: Wallet,
        subItems: [
          {
            id: 'orders',
            ['data-testid']: ORDERS_SIDEBAR_ITEM_TEST_ID,
            title: 'Orders',
            path: '/collections/orders',
            requiredPermission: 'collection_orders:read'
          }
        ],
        requiredPermission: 'collections:read'
      });
    }

    if (!hasOnlySharedAccounts) {
      rootItems.push({
        ['data-testid']: ACCOUNTS_SIDEBAR_ITEM_TEST_ID,
        title: 'Accounts',
        path: '/accounts',
        icon: Landmark,
        subItems: virtualAccountSidebarItems,
        requiredPermission: 'accounts:read'
      });
    }

    rootItems.push({
      ['data-testid']: EXPORTS_SIDEBAR_ITEM_TEST_ID,
      title: 'Exports',
      path: '/exports',
      icon: FileDown,
      subItems: exportsSidebarItems,
      requiredPermission: 'exports:read'
    });

    if (isSelfServiceDeveloperSettingsEnabled) {
      const developersSubItems: SidebarMenuSubItem[] = [
        {
          id: 'developers_authentication',
          ['data-testid']: AUTHENTICATION_SIDEBAR_ITEM_TEST_ID,
          title: 'Authentication',
          path: '/developers/authentication'
        },
        {
          id: 'developers_webhooks',
          ['data-testid']: WEBHOOKS_SIDEBAR_ITEM_TEST_ID,
          title: 'Webhooks',
          path: '/developers/webhooks'
        }
      ];

      if (isMcpTokensEnabled) {
        developersSubItems.push({
          id: 'developers_mcp',
          ['data-testid']: MCP_SIDEBAR_ITEM_TEST_ID,
          title: 'MCP',
          path: '/developers/mcp'
        });
      }

      rootItems.push({
        ['data-testid']: DEVELOPERS_SIDEBAR_ITEM_TEST_ID,
        title: 'Developers',
        path: '/developers',
        icon: CodeXml,
        requiredPermission: 'developers:manage',
        subItems: developersSubItems
      });
    }

    rootItems.push({
      ['data-testid']: USER_MANAGEMENT_SIDEBAR_ITEM_TEST_ID,
      title: 'User management',
      path: '/users',
      end: true,
      icon: UsersRound,
      requiredPermission: 'users:read'
    });

    if (isSelfServiceDeveloperSettingsEnabled) {
      rootItems.push({
        ['data-testid']: CONFIGURATIONS_SIDEBAR_ITEM_TEST_ID,
        title: 'Configurations',
        path: '/configurations',
        icon: SettingsIcon,
        requiredPermission: 'configurations:read',
        subItems: [
          {
            id: 'configurations_business_units',
            ['data-testid']: BUSINESS_UNITS_SIDEBAR_ITEM_TEST_ID,
            title: 'Business units',
            path: '/configurations/business_units'
          }
        ]
      });
    }

    return rootItems;
  };

  const sidebarItems = useMemo(buildMenuItems, [
    isManualPayoutApprovalFlowEnabled,
    isWithdrawalRequestsEnabled,
    isCryptoFundingEnabled,
    hasPayoutProducts,
    hasCollectionProducts,
    hasOnlySharedAccounts,
    sortedAccountsInfo,
    isPayoutDraftApprovalFlowEnabled,
    isBatchesPageEnabled,
    isReceiverManagementEnabled,
    isSelfServiceDeveloperSettingsEnabled,
    isMcpTokensEnabled
  ]);

  const settingsItems = useMemo(() => {
    const rootItems: SidebarMenuItem[] = [];

    rootItems.push({
      ['data-testid']: PROFILE_SIDEBAR_ITEM_TEST_ID,
      title: 'Profile',
      path: '/settings/profile',
      icon: CircleUserRound,
      end: true
    });

    return rootItems;
  }, []);

  return { sidebarItems, settingsItems };
};
